import {
  CURRENT_FEATURE_INDEX,
  FEATURES_BANNER_ENABLED,
  FeaturesBanner,
} from "@/components/atoms/FeaturesBanner/FeaturesBanner";
import { PastDueBanner } from "@/components/atoms/PastDueBanner/PastDueBanner";
import { SubscriptionExpiringBanner } from "@/components/atoms/SubscriptionExpiringBanner/SubscriptionExpiringBanner";
import { mixpanelTrack } from "@/events/mixpanelTrack";
import { cn } from "@/lib/utils";
import { setTopToPage } from "@/redux/globalState/globalStateSlice";
import { AppDispatch } from "@/redux/store";
import {
  canUseClubImportSelector,
  getUserSelector,
  userIsLoggedInSelector,
  userSubscriptionSelector,
} from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

import { NavBarTouchDevice } from "./NavBarTouchDevice/NavBarTouchDevice";
import { MobileTopNav } from "./NavBarTouchDevice/components/MobileTopNav";
import { NavBarMega } from "./siteNav/NavBarMega";

interface NavigationBarProps {
  currentRoute: string;
}

const NavigationBar = ({ currentRoute }: NavigationBarProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector(getUserSelector);
  // Both selectors already fall back to the cached cold-start entitlement
  // (see redux/permissions/coldStart.ts), so no localStorage read is needed here.
  const canUseClubImport = useSelector(canUseClubImportSelector);
  const userIsLoggedIn =
    useSelector(userIsLoggedInSelector) ||
    localStorage.getItem("loggedIn") === "true";
  const userSubscription = useSelector(userSubscriptionSelector);
  const freeUser = useMemo(
    () => !userSubscription || userSubscription === "free",
    [userSubscription],
  );
  const paidUserButNotUltimate = useMemo(
    () => userSubscription === "silver" || userSubscription === "gold",
    [userSubscription],
  );
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);

  const [closedBanner, setClosedBanner] = useState(
    localStorage.getItem(`closed-feature-banner`) ===
      CURRENT_FEATURE_INDEX?.toString(),
  );

  /*
    The bar is absent either because this build has nothing to show in it or
    because the user closed it. The offsets below reserve space for it, so they
    have to key off both — not just the dismissal.
  */
  const noFeaturesBanner = !FEATURES_BANNER_ENABLED || closedBanner;

  const [closedSubExpireBanner, setClosedSubExpireBanner] = useState(
    localStorage.getItem(`closed-subscription-expiring-banner`) === "true",
  );

  const handleCloseBanner = () => {
    localStorage.setItem(
      `closed-feature-banner`,
      CURRENT_FEATURE_INDEX?.toString(),
    );
    setClosedBanner(true);
  };

  const handleCloseSubscriptionExpiringBanner = () => {
    localStorage.setItem(`closed-subscription-expiring-banner`, "true");
    setClosedSubExpireBanner(true);
  };

  const showSubExpireBanner = useMemo(() => {
    if (closedSubExpireBanner || user.subscription === "free") return false;

    const show =
      user.cancelsAt &&
      user.cancelsAt * 1000 - Date.now() < 7 * 24 * 60 * 60 * 1000; // 7 days

    if (show) mixpanelTrack("subscription_expiring_banner_shown");

    return show;
  }, [user.cancelsAt, closedSubExpireBanner]);

  useEffect(() => {
    if (isTouchDevice) {
      if (noFeaturesBanner && !user.pastDue && !showSubExpireBanner) {
        dispatch(
          setTopToPage({
            value: 0,
            tailwindString: "top-0",
            closedTopBar: true,
          }),
        );
      } else {
        dispatch(
          setTopToPage({
            value: 36,
            tailwindString: "top-9",
            closedTopBar: false,
          }),
        );
      }
    } else {
      if (noFeaturesBanner && !user.pastDue && !showSubExpireBanner) {
        dispatch(
          setTopToPage({
            value: 48,
            tailwindString: "top-12",
            closedTopBar: true,
          }),
        );
      } else {
        dispatch(
          setTopToPage({
            value: 84,
            tailwindString: "top-21",
            closedTopBar: false,
          }),
        );
      }
    }
  }, [isTouchDevice, showSubExpireBanner, noFeaturesBanner]);

  return (
    <>
      {user.pastDue ? (
        <PastDueBanner />
      ) : showSubExpireBanner ? (
        <SubscriptionExpiringBanner
          cancelsAt={user.cancelsAt}
          onClose={handleCloseSubscriptionExpiringBanner}
        />
      ) : (
        !noFeaturesBanner && <FeaturesBanner onClose={handleCloseBanner} />
      )}
      {isTouchDevice ? (
        <>
          <MobileTopNav />
          <NavBarTouchDevice
            currentRoute={currentRoute}
            userIsLoggedIn={userIsLoggedIn}
            canUseClubImport={canUseClubImport}
            freeUser={freeUser}
            paidUserButNotUltimate={paidUserButNotUltimate}
            userSubscription={userSubscription}
          />
        </>
      ) : (
        <div
          className={cn(
            "sticky w-full z-40",
            noFeaturesBanner && !user.pastDue ? "top-0" : "top-9",
          )}
        >
          <NavBarMega
            userIsLoggedIn={userIsLoggedIn}
            userSubscription={userSubscription}
            paidUserButNotUltimate={paidUserButNotUltimate}
          />
        </div>
      )}
    </>
  );
};

export default NavigationBar;
